<template>
  <SectionContainer>
    <div class="company-wall-municipality-reviews">
      <div>
        <SHeading
          v-if="loadedReviews.length || isLoadingReviews"
          class="company-wall-municipality-reviews__title"
          level="2"
          size="2"
        >
          {{ tr("main_site.company_wall.municipality.reviews.title") }}
        </SHeading>

        <p class="company-wall-municipality-reviews__description">
          {{ reviewsDescription }}
        </p>
      </div>

      <SReviews
        v-if="loadedReviews.length"
        layout="grid"
        :reviews="loadedReviews"
      />
      <SReviewsSkeleton v-if="isLoadingReviews" />

      <SErrorMessage
        v-else-if="reviewsState.status === 'error'"
        retry
        size="lg"
        @retry="refreshReviews"
      />

      <div class="company-wall-municipality-reviews__actions">
        <SButton v-if="formUrl" :to="formUrl">
          {{ tr("main_site.company_wall.municipality.reviews.cta") }}
        </SButton>
        <SButton
          v-if="showLoadMore"
          action-type="tertiary-soft"
          :loading="reviewsState.status === 'pending'"
          suffix-icon="far fa-chevron-down"
          @click="reviewsPage++"
        >
          {{ tr("main_site.company_wall.municipality.reviews.load_more") }}
        </SButton>
      </div>
    </div>
  </SectionContainer>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { computed, ref, watchEffect } from "vue";

import { SButton } from "@solvari/common-fe";
import {
  SErrorMessage,
  SHeading,
  SReviews,
  SReviewsSkeleton,
} from "@solvari/components";
import {
  injectI18nLocale,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";

import {
  useLocalReviews,
  useLocalReviewStats,
} from "~/features/company-wall/api/localSearch.api.ts";
import SectionContainer from "~/shared/components/SectionContainer.vue";

import { useLocalSearchTranslations } from "../../composables/useLocalSearchTranslations.ts";

const props = defineProps<{
  formUrl: string | null;
  routeParams: {
    geoProductSlug: string;
    municipality: string;
    province: string;
  };
}>();

const localeCode = injectI18nLocale().value;

const { data: reviewStats } = useQuery(
  useLocalReviewStats({
    geoProductCategorySlug: props.routeParams.geoProductSlug,
    localeCode,
    municipalitySlug: props.routeParams.municipality,
  }),
);

const localReviewCount = computed(() => reviewStats.value?.localReviewCount);

const { tr } = useLocalSearchTranslations(useI18nMainSiteCompanyWall, {
  localeCode,
  geoProductSlug: props.routeParams.geoProductSlug,
  localReviewCount,
  municipality: props.routeParams.municipality,
  province: props.routeParams.province,
});

const descriptionKey = computed(() => {
  const geoScope = reviewStats.value?.geoScope ?? "municipality";
  return geoScope === "province"
    ? "main_site.company_wall.municipality.reviews.description_province"
    : "main_site.company_wall.municipality.reviews.description_municipality";
});

const reviewsDescription = computed(() => tr(descriptionKey.value));

const reviewsPage = ref(1);
const loadedReviews = ref<App.DTO.Customer.Review.CustomerReviewDTO[]>([]);

const {
  state: reviewsState,
  refresh: refreshReviews,
  isLoading: isLoadingReviews,
} = useQuery(
  useLocalReviews({
    geoProductCategorySlug: props.routeParams.geoProductSlug,
    localeCode,
    municipalitySlug: props.routeParams.municipality,
    page: reviewsPage,
  }),
);

watchEffect(
  () => {
    if (!reviewsState.value.data?.data) {
      return;
    }
    if (reviewsPage.value === 1) {
      loadedReviews.value = reviewsState.value.data.data;
    } else if (reviewsPage.value === reviewsState.value.data.current_page) {
      loadedReviews.value.push(...reviewsState.value.data.data);
    }
  },
  { flush: "sync" },
);

const showLoadMore = computed(() => !!reviewsState.value.data?.next_page_url);
</script>

<style lang="postcss">
.company-wall-municipality-reviews {
  &__title {
    @apply text-center;
  }
  &__description {
    @apply mb-4 text-center text-lg leading-7 text-slate-600;
  }
  &__actions {
    @apply flex w-full flex-col-reverse items-center justify-center gap-4;

    @screen 700 {
      @apply flex-row;
    }
  }
}
</style>
